/**
 * Oriented bounding boxes — the car's collision shape, and the sphere test the
 * car-ball contact is built on.
 *
 * Rocket League's car hitbox is a single box, rigidly attached to the car's
 * orientation and offset from the car's origin (the origin sits at the centre of
 * MASS, the box does not — see OCTANE_HITBOX_OFFSET). Everything here is
 * expressed in the frame documented in math.ts: the box's local axes are the
 * COLUMNS of its orientation matrix, so for a car they are (forward, left, up).
 *
 * The whole test is done by transforming the query point into the box's local
 * frame, where the box is axis-aligned and the closest point is a per-axis
 * clamp. There are no cross products anywhere in this file, so nothing in it is
 * handedness-sensitive (docs/PHYSICS_REFERENCE.md §6.3).
 *
 * The box comes from the car's BODY (./car-config), so `carHitbox` and
 * `carHitboxInto` take a `CarConfig`. It defaults to `OCTANE_CONFIG`, which is
 * what every caller in this tree meant before there were other bodies.
 */

import { OCTANE_CONFIG, type CarConfig } from './car-config';
import { OCTANE_HITBOX } from './constants';
import {
  add,
  col,
  identity3,
  length,
  type Mat3,
  mulVec,
  mulVecT,
  scale,
  sub,
  type Vec3,
  vec3,
} from './math';
import type { Contact } from './surface';

export type { Contact };

/** A box, centred at `center`, with local axes given by the columns of
 *  `orientation` and HALF extents `halfExtents` along those axes. */
export interface OBB {
  /** World-space centre of the BOX. For a car this is not the car's origin. */
  center: Vec3;
  /** Columns are the box's local axes in world space (car: forward, left, up). */
  orientation: Mat3;
  /** Half extents along local x, y, z. */
  halfExtents: Vec3;
}

/** Half extents of the Octane hitbox. OCTANE_HITBOX is stated as FULL extents
 *  (length x width x height), so everything here halves it exactly once. */
export const OCTANE_HALF_EXTENTS: Readonly<Vec3> = Object.freeze(
  vec3(OCTANE_HITBOX.length / 2, OCTANE_HITBOX.width / 2, OCTANE_HITBOX.height / 2)
);

const halfExtentsCache = new WeakMap<CarConfig, Readonly<Vec3>>();

/** Half extents of a body's hitbox. `hitboxSize` is stated as FULL extents, so
 *  this is the ONE place in the tree that halves it. Memoized per config so the
 *  per-tick paths do no arithmetic they do not have to. */
export const carHalfExtents = (config: CarConfig): Readonly<Vec3> => {
  const cached = halfExtentsCache.get(config);
  if (cached !== undefined) return cached;
  const s = config.hitboxSize;
  const half = Object.freeze(vec3(s.x / 2, s.y / 2, s.z / 2));
  halfExtentsCache.set(config, half);
  return half;
};

/**
 * The car's collision box in world space.
 *
 * The hitbox centre is the car's origin plus the hitbox offset ROTATED into
 * world space — the offset is a local-frame vector, so a car on a wall has its
 * box offset along the wall normal, not along +Z.
 */
export const carHitbox = (
  position: Vec3,
  orientation: Mat3,
  config: CarConfig = OCTANE_CONFIG
): OBB => {
  const o = config.hitboxOffset;
  const h = carHalfExtents(config);
  return {
    center: add(position, mulVec(orientation, vec3(o.x, o.y, o.z))),
    orientation,
    halfExtents: vec3(h.x, h.y, h.z),
  };
};

/**
 * `carHitbox` writing into a caller-owned box, for the per-tick paths that must
 * not allocate. `out.orientation` is REBOUND to the car's own matrix rather than
 * copied, so the box is only valid while that matrix is; nothing here or in
 * ./car-arena mutates an orientation through a box.
 */
export const carHitboxInto = (
  out: OBB,
  position: Vec3,
  orientation: Mat3,
  config: CarConfig = OCTANE_CONFIG
): OBB => {
  const o = config.hitboxOffset;
  const h = carHalfExtents(config);
  out.center.x = position.x + orientation[0] * o.x + orientation[1] * o.y + orientation[2] * o.z;
  out.center.y = position.y + orientation[3] * o.x + orientation[4] * o.y + orientation[5] * o.z;
  out.center.z = position.z + orientation[6] * o.x + orientation[7] * o.y + orientation[8] * o.z;
  out.orientation = orientation;
  out.halfExtents.x = h.x;
  out.halfExtents.y = h.y;
  out.halfExtents.z = h.z;
  return out;
};

/** An empty box for `carHitboxInto` to fill. */
export const makeCarHitbox = (): OBB => ({
  center: vec3(0, 0, 0),
  orientation: identity3(),
  halfExtents: vec3(OCTANE_HALF_EXTENTS.x, OCTANE_HALF_EXTENTS.y, OCTANE_HALF_EXTENTS.z),
});

// ---------------------------------------------------------------------------
// Box vs box
// ---------------------------------------------------------------------------

/**
 * The minimum-translation contact between two boxes: which way to push them
 * apart, and how far.
 */
export interface OBBOverlap {
  /** Unit axis of least penetration, pointing OUT of `a` toward `b`. */
  normal: Vec3;
  /** How far along `normal` the two boxes overlap. Always > 0 on a hit. */
  depth: number;
}

export const makeOBBOverlap = (): OBBOverlap => ({ normal: vec3(0, 0, 0), depth: 0 });

/**
 * A cross-product axis whose length is below this is degenerate — the two edge
 * directions are parallel, the axis carries no information, and the pair of face
 * axes perpendicular to it already covers the case. Dividing by it would
 * manufacture an enormous spurious penetration depth, so it is skipped.
 * A tolerance on |a_i x b_j| = sin(angle between them), not a physical quantity.
 * [Ericson, Real-Time Collision Detection, §4.4.1]
 */
const SAT_PARALLEL_EPSILON = 1e-6;

/** Scratch for the 3x3 axis-dot matrix, so the boolean path allocates nothing. */
const _r = [0, 0, 0, 0, 0, 0, 0, 0, 0];
const _absR = [0, 0, 0, 0, 0, 0, 0, 0, 0];
const _t = [0, 0, 0];
const _ah = [0, 0, 0];
const _bh = [0, 0, 0];

/**
 * Separating-axis test between two oriented boxes: 3 face axes each, plus the 9
 * edge-edge cross products. Returns true when no separating axis exists, i.e.
 * the boxes intersect.
 *
 * With `out` given it also reports the MINIMUM-TRANSLATION contact — the axis on
 * which the overlap is smallest, oriented out of `a` toward `b`, and that
 * overlap as `depth`. Without it the test short-circuits on the first separating
 * axis and touches no scratch beyond the projections, which is what the cheap
 * `carsOverlap` predicate in ./demo wants.
 *
 * The edge-edge axes are compared in NORMALIZED units. The classic optimized
 * form (Ericson §4.4.1) leaves `a_i x b_j` un-normalized, which is harmless for
 * a yes/no answer and completely wrong for a depth comparison: an axis at 30
 * degrees would have its overlap scaled by 0.5 against a face axis's 1.0 and win
 * ties it should lose. `|a_i x b_j| = sqrt(1 - (a_i . b_j)^2)` because both are
 * unit vectors, so the normalization costs one square root on the axes that are
 * not skipped outright.
 */
export const obbOverlap = (a: OBB, b: OBB, out?: OBBOverlap): boolean => {
  const am = a.orientation;
  const bm = b.orientation;
  _ah[0] = a.halfExtents.x;
  _ah[1] = a.halfExtents.y;
  _ah[2] = a.halfExtents.z;
  _bh[0] = b.halfExtents.x;
  _bh[1] = b.halfExtents.y;
  _bh[2] = b.halfExtents.z;

  // r[i*3+j] = cos of the angle between a's axis i and b's axis j. Columns of
  // the orientation matrices are the axes, so a_i . b_j is a column dot column.
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      const v = am[i] * bm[j] + am[3 + i] * bm[3 + j] + am[6 + i] * bm[6 + j];
      _r[i * 3 + j] = v;
      _absR[i * 3 + j] = Math.abs(v);
    }
  }

  // Centre-to-centre offset, in a's frame.
  const dx = b.center.x - a.center.x;
  const dy = b.center.y - a.center.y;
  const dz = b.center.z - a.center.z;
  for (let i = 0; i < 3; i++) _t[i] = am[i] * dx + am[3 + i] * dy + am[6 + i] * dz;

  let best = Infinity;
  // 0..2 = face axis of a, 3..5 = face axis of b, 6..14 = a_i x b_j.
  let bestAxis = -1;
  let bestSign = 1;

  // Face axes of a.
  for (let i = 0; i < 3; i++) {
    const ra = _ah[i];
    const rb = _bh[0] * _absR[i * 3] + _bh[1] * _absR[i * 3 + 1] + _bh[2] * _absR[i * 3 + 2];
    const overlap = ra + rb - Math.abs(_t[i]);
    if (overlap <= 0) return false;
    if (out !== undefined && overlap < best) {
      best = overlap;
      bestAxis = i;
      bestSign = _t[i] < 0 ? -1 : 1;
    }
  }

  // Face axes of b.
  for (let j = 0; j < 3; j++) {
    const tb = _t[0] * _r[j] + _t[1] * _r[3 + j] + _t[2] * _r[6 + j];
    const ra = _ah[0] * _absR[j] + _ah[1] * _absR[3 + j] + _ah[2] * _absR[6 + j];
    const overlap = ra + _bh[j] - Math.abs(tb);
    if (overlap <= 0) return false;
    if (out !== undefined && overlap < best) {
      best = overlap;
      bestAxis = 3 + j;
      bestSign = tb < 0 ? -1 : 1;
    }
  }

  // Edge-edge axes: a_i x b_j, in the index-cycled form so all nine cases share
  // one expression.
  for (let i = 0; i < 3; i++) {
    const i1 = (i + 1) % 3;
    const i2 = (i + 2) % 3;
    for (let j = 0; j < 3; j++) {
      const j1 = (j + 1) % 3;
      const j2 = (j + 2) % 3;
      const ra = _ah[i1] * _absR[i2 * 3 + j] + _ah[i2] * _absR[i1 * 3 + j];
      const rb = _bh[j1] * _absR[i * 3 + j2] + _bh[j2] * _absR[i * 3 + j1];
      const tl = _t[i2] * _r[i1 * 3 + j] - _t[i1] * _r[i2 * 3 + j];
      // |a_i x b_j| = sin of the angle between them.
      const lenSq = 1 - _r[i * 3 + j] * _r[i * 3 + j];
      const len = lenSq > 0 ? Math.sqrt(lenSq) : 0;
      if (len <= SAT_PARALLEL_EPSILON) continue;
      const overlap = ra + rb - Math.abs(tl);
      if (overlap <= 0) return false;
      if (out !== undefined && overlap / len < best) {
        best = overlap / len;
        bestAxis = 6 + i * 3 + j;
        bestSign = tl < 0 ? -1 : 1;
      }
    }
  }

  if (out === undefined) return true;

  // Rebuild the winning axis. Only now, and only one of the fifteen.
  if (bestAxis < 3) {
    out.normal.x = am[bestAxis] * bestSign;
    out.normal.y = am[3 + bestAxis] * bestSign;
    out.normal.z = am[6 + bestAxis] * bestSign;
  } else if (bestAxis < 6) {
    const j = bestAxis - 3;
    out.normal.x = bm[j] * bestSign;
    out.normal.y = bm[3 + j] * bestSign;
    out.normal.z = bm[6 + j] * bestSign;
  } else {
    const i = Math.floor((bestAxis - 6) / 3);
    const j = (bestAxis - 6) % 3;
    const ax = am[i];
    const ay = am[3 + i];
    const az = am[6 + i];
    const bx = bm[j];
    const by = bm[3 + j];
    const bz = bm[6 + j];
    const cx = ay * bz - az * by;
    const cy = az * bx - ax * bz;
    const cz = ax * by - ay * bx;
    const inv = bestSign / Math.hypot(cx, cy, cz);
    out.normal.x = cx * inv;
    out.normal.y = cy * inv;
    out.normal.z = cz * inv;
  }
  out.depth = best;
  return true;
};

/** World-space point -> box-local coordinates. */
export const toLocal = (box: OBB, p: Vec3): Vec3 => mulVecT(box.orientation, sub(p, box.center));

/** Box-local coordinates -> world-space point. */
export const toWorld = (box: OBB, local: Vec3): Vec3 => add(box.center, mulVec(box.orientation, local));

const clampAbs = (v: number, limit: number): number => (v < -limit ? -limit : v > limit ? limit : v);

/**
 * Closest point of the SOLID box to `p`. When `p` is inside the box this is `p`
 * itself — that is the standard definition and it is what makes the distance
 * function correct, but it means "inside" has to be handled separately by any
 * caller that needs a push-out direction. `obbSphereContact` does exactly that.
 */
export const closestPointOnOBB = (box: OBB, p: Vec3): Vec3 => {
  const local = toLocal(box, p);
  const h = box.halfExtents;
  return toWorld(box, vec3(clampAbs(local.x, h.x), clampAbs(local.y, h.y), clampAbs(local.z, h.z)));
};

/** True when `p` is inside or exactly on the surface of the box. */
export const obbContainsPoint = (box: OBB, p: Vec3): boolean => {
  const local = toLocal(box, p);
  const h = box.halfExtents;
  return Math.abs(local.x) <= h.x && Math.abs(local.y) <= h.y && Math.abs(local.z) <= h.z;
};

/** Distance from `p` to the box; zero when `p` is inside. */
export const obbDistanceToPoint = (box: OBB, p: Vec3): number =>
  length(sub(p, closestPointOnOBB(box, p)));

/** Cheap overlap test — the same predicate as `obbSphereContact() !== null`,
 *  without building the contact. */
export const obbOverlapsSphere = (box: OBB, center: Vec3, radius: number): boolean =>
  obbDistanceToPoint(box, center) <= radius;

/**
 * Contact between the box and a sphere, or null when they do not overlap.
 *
 * `normal` points OUT of the box toward the sphere centre and `depth` is the
 * distance the SPHERE must move along `normal` to just touch — matching the
 * `Contact` contract in ./surface.
 *
 * Two regimes:
 *   - sphere centre outside the box: normal is the direction from the closest
 *     surface point to the centre, depth = radius - distance. This is the face,
 *     edge and corner case all at once; the clamp decides which.
 *   - sphere centre inside the box (a deep hit, e.g. a car driving through a
 *     ball at a low tick rate): there is no such direction, so the sphere is
 *     pushed out along the axis it is LEAST deep on, giving depth =
 *     radius + (halfExtent - |local|). Picking any other axis would teleport the
 *     ball through the car.
 */
export const obbSphereContact = (box: OBB, center: Vec3, radius: number): Contact | null => {
  const local = toLocal(box, center);
  const h = box.halfExtents;

  const penX = h.x - Math.abs(local.x);
  const penY = h.y - Math.abs(local.y);
  const penZ = h.z - Math.abs(local.z);

  if (penX >= 0 && penY >= 0 && penZ >= 0) {
    // Centre inside the box: push out along the least-penetrated local axis.
    let axis: 0 | 1 | 2 = 0;
    let pen = penX;
    if (penY < pen) {
      axis = 1;
      pen = penY;
    }
    if (penZ < pen) {
      axis = 2;
      pen = penZ;
    }

    const comp = axis === 0 ? local.x : axis === 1 ? local.y : local.z;
    // A centre exactly on the box's mid-plane has no preferred side; +1 keeps
    // the result deterministic rather than sign-of-zero dependent.
    const s = comp < 0 ? -1 : 1;

    const surface = vec3(local.x, local.y, local.z);
    if (axis === 0) surface.x = s * h.x;
    else if (axis === 1) surface.y = s * h.y;
    else surface.z = s * h.z;

    return {
      point: toWorld(box, surface),
      normal: scale(col(box.orientation, axis), s),
      depth: radius + pen,
    };
  }

  const closestLocal = vec3(clampAbs(local.x, h.x), clampAbs(local.y, h.y), clampAbs(local.z, h.z));
  const point = toWorld(box, closestLocal);
  const d = sub(center, point);
  const dist = length(d);
  if (dist > radius) return null;

  // dist === 0 is impossible here: the centre is outside the box, so the
  // clamped point differs from it on at least one axis.
  return { point, normal: scale(d, 1 / dist), depth: radius - dist };
};
